import React, { useCallback, useEffect, useState } from 'react';
import { CSVLink } from 'react-csv';
import {
  Form,
  Button,
  Typography,
  Space,
  notification,
  Tag,
  Row,
  Col,
  Modal
} from 'antd';
import { Link } from 'react-router-dom';
import TableWithSorter from '../../components/Common/TableWithSorter';
import {
  fetchCommunityApplication,
  getCommunities,
  submitApplicationStatus,
  submitManualApplication,
  updateApplication
} from '../../services/communityService';
import { getDaysDifferenceBetweenDates } from '../../utilities/dateHelper';
import CommunitiesApplicationForm from './components/CommunitiesApplicationForm.jsx';
import CommunitiesApplicationModal from './components/CommunitiesApplicationModal.jsx';
import CommunitiesEnrolment from './components/CommunitiesEnrolment';
import {
  applicationStatus,
  defaultPageSize,
  sortOrderList
} from './constant';
import {
  getCancellationData,
  getformattedApplicationForCSV,
  isInternalEmail
} from './utils';
import Text from 'antd/lib/typography/Text';

const { Title } = Typography;

const CommunitiesApplicationPage = () => {
  const [form] = Form.useForm();
  const [statusForm] = Form.useForm();
  const [enrollmentForm] = Form.useForm();

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [totalCount, setTotalCount] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [communityList, setCommunityList] = useState([]);

  const [filters, setFilters] = useState({
    communityCode: null,
    sortOrder: sortOrderList[0].value,
    startDate: null,
    endDate: null,
    sortBy: 'createdAt',
    status: null
  });
  const [applicationList, setApplicationList] = useState(false);
  const [activeApplication, setActiveApplication] = useState(null);

  const [isEnrolmentModalOpen, setIsEnrolmentModalOpen] = useState(false);
  const [csvDataList, setCsvDataList] = useState([]);
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  const [isCsvDataLoading, setIsCsvDataLoading] = useState(false);

  const columns = [
    {
      title: 'No',
      dataIndex: 'key',
      key: 'key',
      render: (value, item, index) => (pageNo - 1) * 10 + index + 1
    },
    {
      title: 'Full Name',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (text, record) => (
        <Space size="middle">
          <Button type="link" onClick={() => setActiveApplication(record)}>
            {text}
          </Button>
        </Space>
      )
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Subscription Id',
      dataIndex: ['payments', 'subscriptionId'],
      key: 'subscriptionId',
      skipInjectedSorter: true,
      sorter: (a, b) =>
        a?.payments?.subscriptionId - b?.payments?.subscriptionId
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) =>
        renderApplicationStatusTag({
          text,
          status: record?.status
        })
    },
    {
      title: 'Member Type',
      dataIndex: 'memberType',
      key: 'memberType',
      render: (text, record) => {
        if (isInternalEmail({ email: record?.email }))
          return <Tag color="orange">Nas Academy</Tag>;

        if (record?.payment_details) return <Tag color="cyan">Paid</Tag>;
      }
    },
    {
      title: 'Unsubscribed',
      dataIndex: 'unsubscribed',
      key: 'unsubscribed',
      render: (text, record) => {
        const cancellationData = getCancellationData({
          application: record
        });

        if (cancellationData?.isCancelled)
          return <Tag color="default">Unsubscribed</Tag>;

        return '-';
      }
    },
    {
      title: 'Pending Day(s)',
      dataIndex: 'createdAt',
      key: 'pendingDays',
      render: (_, record) => {
        const isPending = record.status === applicationStatus.PENDING;

        const pendingDays = getDaysDifferenceBetweenDates({
          date1: new Date(),
          date2: record?.createdAt
        });

        return isPending ? `${pendingDays} days` : '-';
      },
      skipInjectedSorter: true,
      sorter: (a, b) => {
        const isAPending = a.status === applicationStatus.PENDING;
        const isBPending = b.status === applicationStatus.PENDING;

        const pendingADays = getDaysDifferenceBetweenDates({
          date1: new Date(),
          date2: a?.createdAt
        });

        const pendingBDays = getDaysDifferenceBetweenDates({
          date1: new Date(),
          date2: b?.createdAt
        });

        const offsetADays = isAPending ? pendingADays : -1;
        const offsetBDays = isBPending ? pendingBDays : -1;

        return offsetADays - offsetBDays;
      }
    },
    {
      title: 'Status Updated At',
      dataIndex: 'statusUpdatedAt',
      key: 'statusUpdatedAt'
    },
    {
      title: 'Notes',
      dataIndex: 'reviewerNotes',
      key: 'reviewerNotes',
      width: 400
    },
    {
      title: 'Learner Id',
      dataIndex: 'learnerId',
      key: 'learnerId',
      render: (text) => <Link to={`/learner/${text}`}>{text}</Link>
    },
    {
      title: 'Country Field',
      dataIndex: ['payments', 'country'],
      key: 'country'
    },
    {
      title: 'Price',
      key: 'price',
      render: (_, record) => {
        return `${record?.payments?.local_currency} ${Math.round(
          record?.payments?.local_amount / 100
        )}`;
      }
    },
    {
      title: 'Followers',
      dataIndex: 'followersCount',
      key: 'followersCount'
    },
    {
      title: 'Payment Date',
      dataIndex: ['payments', 'createdAt'],
      key: 'paymentDate'
    }
  ];

  const renderApplicationStatusTag = ({ status, text }) => {
    const tagColor =
      status === applicationStatus.APPROVE
        ? 'green'
        : status === applicationStatus.REJECT
        ? 'volcano'
        : 'blue';
    return <Tag color={tagColor}>{text}</Tag>;
  };

  const onPaginationChange = (data) => {
    setPageNo(data.current);
    setPageSize(data.pageSize);
  };

  const getCommunityApplications = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        pageNo,
        pageSize,
        ...filters
      };

      const { data, error } = await fetchCommunityApplication(params);

      if (error) throw new Error(error);

      setApplicationList(data.paginatedList);
      setTotalCount(data.totalCount);
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsLoading(false);
    }
  }, [pageNo, pageSize, filters]);

  const getCSVDataList = async () => {
    try {
      setIsCsvDataLoading(true);
      const { data, error } = await fetchCommunityApplication({
        ...filters,
        communityCode: form.getFieldValue('communityCode')
      });

      if (error) throw new Error(error);

      const formattedList = getformattedApplicationForCSV({
        list: data.paginatedList
      });
      setCsvDataList(formattedList);
      setIsCsvModalOpen(true);
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsCsvDataLoading(false);
    }
  };

  const onFinish = (values) => {
    setPageNo(1);
    if (
      new Date(values?.startDate).getTime() >
      new Date(values?.endDate).getTime()
    ) {
      notification['error']({
        message: 'Error',
        description: 'End Date cannot be before Start Date'
      });
      return;
    }

    const statusFilter =
      values?.status === applicationStatus.CANCELLED
        ? { subscriptionStatus: values?.status }
        : { status: values?.status };

    const filterData = {
      communityCode: values.communityCode ?? communityList[0].value,
      sortOrder: values.sortOrder ?? sortOrderList[0].value,
      startDate: values.startDate,
      endDate: values.endDate,
      sortBy: 'createdAt',
      applicationSearch: values.applicationSearch,
      ...statusFilter
    };

    setFilters(filterData);
  };

  const resetForm = () => {
    form.resetFields();
    form.submit();
  };

  const clearActiveApplication = () => {
    setActiveApplication(null);
  };

  const onStatusSubmit = async ({ status }) => {
    try {
      setIsStatusLoading(true);
      const params = {
        _id: activeApplication?._id,
        communityCode: activeApplication?.communityCode,
        status,
        reviewerNotes: statusForm.getFieldValue('reviewerNotes')
      };

      const { data, error } = await submitApplicationStatus(params);

      if (error) throw new Error(error);

      notification['success']({
        message: 'Success',
        description: data.message
      });

      const updatedActiveApplication = { ...activeApplication, status };
      const updatedApplicationList = applicationList?.map(
        (application) => {
          if (application?._id === activeApplication?._id)
            return updatedActiveApplication;

          return application;
        }
      );
      setApplicationList(updatedApplicationList);
      setActiveApplication(updatedActiveApplication);
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsStatusLoading(false);
    }
  };

  const onEnrollmentSubmit = async (values) => {
    try {
      setIsStatusLoading(true);
      const { error } = await submitManualApplication(values);
      if (error) throw new Error(error);
      notification['success']({
        message: 'Success',
        description: `Manual enrolment added!`
      });
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsStatusLoading(false);
    }
  };

  const updateCommunityApplication = async (values) => {
    try {
      setIsStatusLoading(true);
      const { data, error } = await updateApplication(values);
      if (error) throw new Error(error);

      notification['success']({
        message: 'Success',
        description: `Application updated!`
      });

      const updatedActiveApplication = data;
      const updatedApplicationList = applicationList?.map(
        (application) => {
          if (application?._id === activeApplication?._id)
            return {
              ...updatedActiveApplication,
              payments: application.payments
            };

          return application;
        }
      );
      setApplicationList(updatedApplicationList);
      setActiveApplication(updatedActiveApplication);
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsStatusLoading(false);
    }
  };

  const updateReviewersNote = async () => {
    await updateCommunityApplication({
      _id: activeApplication?._id,
      reviewerNotes: statusForm.getFieldValue('reviewerNotes')
    });
  };

  const updateApplicationField = async (values) => {
    await updateCommunityApplication({
      _id: activeApplication?._id,
      ...values
    });
  };

  const getCommunityList = useCallback(async () => {
    try {
      setIsLoading(true);
      const values = await getCommunities({ pageNo: 1, pageSize: 1000 });
      const result = values?.data?.paginatedList?.map((community) => ({
        value: community?.code,
        text: community?.title
      }));
      const communities = result.sort((communityA, communityB) =>
        communityA?.text?.localeCompare(communityB?.text)
      );
      setCommunityList(communities);
      setFilters((prev) => {
        return {
          ...prev,
          communityCode: communities?.[0]?.value
        };
      });
      form.setFieldsValue({ communityCode: communities?.[0]?.value });
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsLoading(true);
    }
  }, [form]);

  useEffect(() => {
    if (communityList?.length > 0 && filters?.communityCode) {
      getCommunityApplications();
    }
  }, [getCommunityApplications, communityList, filters?.communityCode]);

  useEffect(() => {
    getCommunityList();
  }, [getCommunityList]);

  return (
    <div>
      <div>
        <Row justify="space-between" align="middle">
          <Col>
            <Title>Communities Applications</Title>
          </Col>
          <Col>
            <Row gutter={16}>
              <Col>
                <Button
                  type="primary"
                  onClick={() => setIsEnrolmentModalOpen(true)}
                  centered>
                  Create Manual Enrolment
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  centered
                  onClick={getCSVDataList}
                  loading={isCsvDataLoading}>
                  Export CSV
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
        <CommunitiesApplicationForm
          onFinish={onFinish}
          resetForm={resetForm}
          form={form}
          totalCount={totalCount}
          communityList={communityList}
        />
        <TableWithSorter
          tableLayout="auto"
          scroll={{ x: 1000 }}
          expandable={{ defaultExpandAllRows: true }}
          loading={isLoading}
          dataSource={applicationList}
          columns={columns}
          pagination={{
            total: totalCount,
            defaultPageSize: defaultPageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '50']
          }}
          onChange={onPaginationChange}
        />
        <CommunitiesApplicationModal
          activeApplication={activeApplication}
          clearActiveApplication={clearActiveApplication}
          onStatusSubmit={onStatusSubmit}
          isStatusLoading={isStatusLoading}
          renderApplicationStatusTag={renderApplicationStatusTag}
          statusForm={statusForm}
          updateReviewersNote={updateReviewersNote}
          updateApplicationField={updateApplicationField}
        />
        <CommunitiesEnrolment
          isEnrolmentModalOpen={isEnrolmentModalOpen}
          setIsEnrolmentModalOpen={setIsEnrolmentModalOpen}
          isStatusLoading={isStatusLoading}
          enrollmentForm={enrollmentForm}
          onEnrollmentSubmit={onEnrollmentSubmit}
          communityList={communityList}
        />
        <Modal
          visible={isCsvModalOpen}
          onCancel={() => setIsCsvModalOpen(false)}
          footer={null}
          centered>
          <Row style={{ marginBottom: '16px' }}>
            <Text strong>
              Note: The filters are applied on the CSV data
            </Text>
          </Row>
          <Row>
            <Button type="primary">
              <CSVLink data={csvDataList} filename="communityApplications">
                Download CSV File
              </CSVLink>
            </Button>
          </Row>
        </Modal>
      </div>
    </div>
  );
};

export default CommunitiesApplicationPage;
